/* eslint-disable react/no-array-index-key */
/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  FormControl,
  Grid,
  Box,
  IconButton,
  Alert,
  Tooltip,
  Typography,
  TextField,
  Button,
} from '@mui/material';
import ERROR_TYPES from 'modules/common/constants/error-types';
import { selectOrganizationId } from 'modules/common/auth/selectors';
import { selectNotification } from 'modules/common/notifications/selectors';
import { Trash, X, Save } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { FieldArray, Formik } from 'formik';
import { Modal } from 'modules/common/components';
import { scenarioActions } from 'modules/scenario/slice';
import { updateScenarioValidation } from 'modules/scenario/validation/scenario-form-validation';
import { isEmpty } from 'modules/common/helpers/object';
import { selectVisuals } from 'modules/scenario/selectors';
import UpdateFilterModal from './update-filter-model';
import VisualSearch from '../visual-search';
//
const FormStyles = { borderRadius: 10 };
/**
 * Form to update scenario
 * @param {*} param0
 * @returns
 */
const UpdateScenarioForm = ({ show, isShow, scenario }) => {
  const dispatch = useDispatch();
  //
  const organizationId = useSelector(selectOrganizationId);
  const notification = useSelector(selectNotification);
  const visuals = useSelector(selectVisuals);
  //
  const [isDelete, setDelete] = useState(false);
  const [isUpdate, setUpdate] = useState(false);
  const [filterModal, setFilterModal] = useState({
    open: false,
    visualIndex: null,
  });
  const [initialValues, setInitialValues] = useState({
    name: '',
    description: '',
    visuals: [
      {
        displayName: '',
        filters: null,
      },
    ],
  });
  const formElement = useRef(null);
  //
  useEffect(() => {
    if (notification?.isEnabled && notification?.type === ERROR_TYPES.SUCCESS) {
      isShow(false);
    }
  }, [notification]);
  //
  useEffect(() => {
    if (!isEmpty(scenario)) {
      setInitialValues({
        name: scenario.name,
        description: scenario.description,
        visuals: [...scenario.visuals],
      });
    }
  }, [scenario]);
  //
  const deleteScenario = (payload) => {
    dispatch(scenarioActions.deleteScenario(payload));
    setDelete(false);
  };
  //
  const onSubmitUpdateScenario = async (values) => {
    const valueObj = {
      name: values.name,
      description: values.description,
      visuals: values.visuals.map((visual) => ({
        displayName: visual.displayName,
        filters: visual.filters,
      })),
    };
    dispatch(
      scenarioActions.updateScenario({ ...valueObj, organizationId, scenarioId: scenario.id })
    );
  };
  //
  return (
    <Box
      xs={12}
      md={4}
      ml={4}
      mt={2}
      component={Grid}
      container
      item
      display="flex"
      direction="column"
      sx={{ backgroundColor: 'white', flexShrink: 0, display: show ? 'flex' : 'none' }}
    >
      Update Scenario
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={updateScenarioValidation}
        onSubmit={onSubmitUpdateScenario}
      >
        {({
          errors,
          dirty,
          handleBlur,
          handleChange,
          handleSubmit,
          handleReset,
          setFieldValue,
          isSubmitting,
          touched,
          values,
        }) => (
          <form noValidate onSubmit={handleSubmit}>
            <Grid container direction="row" justifyContent="space-between" px={2} mb={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {scenario?.name}
              </Typography>
              <Grid>
                <Tooltip title="Update">
                  <IconButton
                    disabled={isSubmitting}
                    onClick={() =>
                      errors.name || errors.order || errors.tags ? '' : setUpdate(true)
                    }
                    color="inherit"
                    size="small"
                  >
                    <Save />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton color="error" size="small" onClick={() => setDelete(true)}>
                    <Trash />
                  </IconButton>
                </Tooltip>
                <IconButton color="inherit" size="small" onClick={() => isShow(false)}>
                  <X />
                </IconButton>
              </Grid>
            </Grid>
            <Grid container spacing={2} direction="column">
              {notification?.isEnabled && notification?.type === ERROR_TYPES.ERROR && (
                <Alert mt={2} mb={3} severity={notification?.type}>
                  {notification?.message}
                </Alert>
              )}
              <Grid justifyContent="space-around" alignItems="center" px={4} my={4}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="name"
                    value={values.name}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                    fullWidth
                    onBlur={handleBlur}
                    onChange={handleChange}
                    my={2}
                    label="Scenario Name"
                    size="small"
                  />
                </FormControl>
              </Grid>
              <Grid justifyContent="space-around" alignItems="center" px={4} my={4}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="description"
                    value={values.description}
                    error={Boolean(touched.description && errors.description)}
                    helperText={touched.description && errors.description}
                    fullWidth
                    onBlur={handleBlur}
                    onChange={handleChange}
                    my={2}
                    label="Description"
                    size="small"
                  />
                </FormControl>
              </Grid>
              <Grid justifyContent="space-around" alignItems="center" px={4} my={2}>
                <Typography>Visuals</Typography>
                <UpdateFilterModal
                  open={filterModal.open}
                  onClose={() => setFilterModal({ open: false, visualIndex: null })}
                  visualIndex={filterModal.visualIndex}
                  filters={values.visuals?.[filterModal.visualIndex]?.filters}
                  setFieldValue={setFieldValue}
                />
                <FieldArray name="visuals">
                  {({ push, remove }) => (
                    <>
                      {values.visuals.map((visual, index) => (
                        <Box
                          key={`${visual?.displayName}_${index}`}
                          sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}
                        >
                          <VisualSearch
                            value={visual?.displayName}
                            visuals={visuals}
                            onChange={(value) => {
                              setFieldValue(`visuals.${index}.displayName`, value);
                            }}
                            error={Boolean(
                              errors.visuals?.[index]?.displayName &&
                                touched.visuals?.[index]?.displayName
                            )}
                            helperText={
                              touched.visuals?.[index]?.displayName &&
                              errors.visuals?.[index]?.displayName
                            }
                          />
                          <Box my={3}>
                            <Button
                              color="info"
                              variant="contained"
                              disabled={!visual?.displayName}
                              onClick={() => {
                                setFilterModal({
                                  open: true,
                                  visualIndex: index,
                                });
                              }}
                            >
                              Update Filters
                            </Button>
                          </Box>
                          <Button color="error" onClick={() => remove(index)}>
                            Remove
                          </Button>
                        </Box>
                      ))}

                      <Button
                        variant="outlined"
                        onClick={() =>
                          push({
                            displayName: '',
                            filters: null,
                          })
                        }
                      >
                        Add Visual
                      </Button>
                    </>
                  )}
                </FieldArray>
              </Grid>

              <Modal
                open={isDelete}
                handleClose={() => setDelete(false)}
                title="Delete Scenario"
                content="Are you sure you want to delete the scenario?"
                handleSuccess={() => deleteScenario({ organizationId, id: scenario?.id })}
                closeLabel="Cancel"
                successLabel="Delete"
                variant="contained"
                color="error"
              />
              <Modal
                open={isUpdate}
                handleClose={() => {
                  if (dirty) {
                    handleReset();
                  }
                  setUpdate(false);
                }}
                title="Update Scenario"
                content="Are you sure you want to update the scenario ?"
                handleSuccess={() => {
                  formElement.current?.click();
                  setUpdate(false);
                }}
                closeLabel="Cancel"
                successLabel="Update"
                variant="contained"
                color="primary"
              />
            </Grid>
            <input ref={formElement} style={{ visibility: 'hidden' }} type="submit" />
          </form>
        )}
      </Formik>
    </Box>
  );
};
//
export default UpdateScenarioForm;
